import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FileText, Download, Eye, Calendar, ClipboardList, Search, Check, ChevronsUpDown } from "lucide-react";
import { format } from "date-fns";
import DatePicker from "@/components/DatePicker";
import jsPDF from "jspdf";

export default function Reports({ user }) {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [viewReportOpen, setViewReportOpen] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Submittal Planned Report states
  const [plannedReportDialogOpen, setPlannedReportDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [plannedReportData, setPlannedReportData] = useState(null);
  const [viewPlannedReportOpen, setViewPlannedReportOpen] = useState(false);

  // Project Assign Form Report states
  const [projectAssignDialogOpen, setProjectAssignDialogOpen] = useState(false);
  const [selectedProjectForAssign, setSelectedProjectForAssign] = useState("");
  const [projectAssignData, setProjectAssignData] = useState(null);
  const [viewProjectAssignOpen, setViewProjectAssignOpen] = useState(false);
  const [projectComboOpen, setProjectComboOpen] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await api.get("/projects");
      setProjects(response.data);
    } catch (error) {
      toast.error("Failed to fetch projects");
      console.error(error);
    }
  };

  const openScheduleReportDialog = () => {
    setSelectedProjectId("");
    setReportData(null);
    setReportDialogOpen(true);
  };

  const fetchScheduleReport = async (projectId) => {
    setLoadingReport(true);
    try {
      const response = await api.get(`/projects/${projectId}/schedule-report`);
      setReportData(response.data);
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch report");
      console.error(error);
      return null;
    } finally {
      setLoadingReport(false);
    }
  };

  const handleViewReport = async () => {
    if (!selectedProjectId) {
      toast.error("Please select a project");
      return;
    }

    const data = await fetchScheduleReport(selectedProjectId);
    if (data) {
      setViewReportOpen(true);
      setReportDialogOpen(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!selectedProjectId) {
      toast.error("Please select a project");
      return;
    }

    const data = await fetchScheduleReport(selectedProjectId);
    if (data) {
      // Create CSV content
      let csvContent = "Project Schedule Tracker Report\n\n";
      csvContent += `Project: ${data.project.name}\n`;
      csvContent += `Project Number: ${data.project.project_number}\n`;
      csvContent += `Generated At: ${format(new Date(data.generated_at), 'MM/dd/yyyy hh:mm a')}\n\n`;

      // Add submittals data
      data.submittals.forEach((submittal) => {
        csvContent += `\nSubmittal: ${submittal.submittal_name}\n`;
        csvContent += `Status: ${submittal.current_status}\n`;
        csvContent += `HOLD: ${submittal.hold_enabled ? 'Yes' : 'No'}${submittal.hold_enabled ? ` (${submittal.hold_reason})` : ''}\n\n`;
        csvContent += "Step,Planning Status,Planned Date,Actual Date,Received Date,Return Status,Submission Complete\n";
        
        submittal.steps.forEach((step) => {
          csvContent += `"${step.step_name}","${step.planning_status}","${step.planned_date || ''}","${step.actual_date || ''}","${step.received_date || ''}","${step.return_status || ''}","${step.submission_complete || ''}"\n`;
        });
        csvContent += "\n";
      });

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Project_Schedule_Report_${data.project.name.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded successfully");
      setReportDialogOpen(false);
    }
  };

  // Submittal Planned Report functions
  const openPlannedReportDialog = () => {
    setStartDate("");
    setEndDate("");
    setPlannedReportData(null);
    setPlannedReportDialogOpen(true);
  };

  const fetchPlannedReport = async (start, end) => {
    setLoadingReport(true);
    try {
      const response = await api.get(`/reports/submittal-planned?start_date=${start}&end_date=${end}`);
      setPlannedReportData(response.data);
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch planned report");
      console.error(error);
      return null;
    } finally {
      setLoadingReport(false);
    }
  };

  const handleViewPlannedReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    if (startDate > endDate) {
      toast.error("Start date must be before end date");
      return;
    }

    const data = await fetchPlannedReport(startDate, endDate);
    if (data) {
      setViewPlannedReportOpen(true);
      setPlannedReportDialogOpen(false);
    }
  };

  const handleDownloadPlannedReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    if (startDate > endDate) {
      toast.error("Start date must be before end date");
      return;
    }

    const data = await fetchPlannedReport(startDate, endDate);
    if (data) {
      // Create CSV content
      let csvContent = "Submittal Planned Report\n\n";
      csvContent += `Date Range: ${format(new Date(data.start_date), 'MM/dd/yyyy')} - ${format(new Date(data.end_date), 'MM/dd/yyyy')}\n`;
      csvContent += `Generated At: ${format(new Date(data.generated_at), 'MM/dd/yyyy hh:mm a')}\n\n`;
      csvContent += "Project Number,Project Name,Submittal Name,Step,Planned Date\n";
      
      data.items.forEach((item) => {
        csvContent += `"${item.project_number}","${item.project_name}","${item.submittal_name}","${item.step_name}","${format(new Date(item.planned_date), 'MM/dd/yyyy')}"\n`;
      });

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Submittal_Planned_Report_${format(new Date(data.start_date), 'yyyyMMdd')}_${format(new Date(data.end_date), 'yyyyMMdd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded successfully");
      setPlannedReportDialogOpen(false);
    }
  };


  // Project Assign Form Report functions
  const openProjectAssignDialog = () => {
    setSelectedProjectForAssign("");
    setProjectAssignData(null);
    setProjectComboOpen(false);
    setProjectAssignDialogOpen(true);
  };

  const fetchProjectAssignReport = async (projectId) => {
    setLoadingReport(true);
    try {
      const response = await api.get(`/reports/project-assign/${projectId}`);
      setProjectAssignData(response.data);
      return response.data;
    } catch (error) {
      toast.error("Failed to fetch project assign report");
      console.error(error);
      return null;
    } finally {
      setLoadingReport(false);
    }
  };

  const handleViewProjectAssign = async () => {
    if (!selectedProjectForAssign) {
      toast.error("Please select a project");
      return;
    }

    const data = await fetchProjectAssignReport(selectedProjectForAssign);
    if (data) {
      setViewProjectAssignOpen(true);
      setProjectAssignDialogOpen(false);
    }
  };

  const handleDownloadProjectAssignPDF = async () => {
    if (!selectedProjectForAssign) {
      toast.error("Please select a project");
      return;
    }

    const data = await fetchProjectAssignReport(selectedProjectForAssign);
    if (data) {
      // Create PDF using jsPDF
      const doc = new jsPDF();
      
      // Check if multi-branch project
      const branchAllocations = data.branch_allocations || [];
      
      // Iterate through each branch allocation to create separate pages
      branchAllocations.forEach((branchAlloc, branchIndex) => {
        // Add new page for subsequent branches
        if (branchIndex > 0) {
          doc.addPage();
        }
        
        // Title
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text("Project Assign Form", 105, 20, { align: "center" });
        
        // Branch subtitle (for multi-branch projects)
        if (data.is_multi_branch) {
          doc.setFontSize(14);
          doc.setTextColor(128, 0, 128); // Purple color
          doc.text(`Branch: ${branchAlloc.branch_name}`, 105, 30, { align: "center" });
          doc.setTextColor(0, 0, 0); // Reset to black
        }
        
        // Generated date
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const dateYPos = data.is_multi_branch ? 38 : 28;
        doc.text(`Generated: ${format(new Date(data.generated_at), 'MM/dd/yyyy hh:mm a')}`, 105, dateYPos, { align: "center" });
        
        // Content
        doc.setFontSize(12);
        let yPos = data.is_multi_branch ? 55 : 45;
        
        // Project Number
        doc.setFont("helvetica", "bold");
        doc.text("Project Number:", 20, yPos);
        doc.setFont("helvetica", "normal");
        doc.text(data.project_number, 70, yPos);
        yPos += 10;
        
        // Project Name
        doc.setFont("helvetica", "bold");
        doc.text("Project Name:", 20, yPos);
        doc.setFont("helvetica", "normal");
        doc.text(data.project_name, 70, yPos);
        yPos += 10;
        
        // Client Name
        doc.setFont("helvetica", "bold");
        doc.text("Client Name:", 20, yPos);
        doc.setFont("helvetica", "normal");
        doc.text(data.client_name, 70, yPos);
        yPos += 10;
        
        // Software (moved to project information section, only on first page)
        if (branchIndex === 0) {
          doc.setFont("helvetica", "bold");
          doc.text("Software:", 20, yPos);
          doc.setFont("helvetica", "normal");
          doc.text(data.software, 70, yPos);
          yPos += 10;
        }
        
        // Branch (for single branch projects only)
        if (!data.is_multi_branch) {
          doc.setFont("helvetica", "bold");
          doc.text("Branch:", 20, yPos);
          doc.setFont("helvetica", "normal");
          doc.text(branchAlloc.branch_name, 70, yPos);
          yPos += 10;
        }
        
        // Allocated Hours (branch-specific)
        doc.setFont("helvetica", "bold");
        doc.text("Allocated Hours:", 20, yPos);
        doc.setFont("helvetica", "normal");
        doc.text(`${branchAlloc.allocated_hours} hours`, 70, yPos);
        yPos += 10;
        
        // Allocated Schedule (branch-specific)
        doc.setFont("helvetica", "bold");
        doc.text("Allocated Schedule:", 20, yPos);
        doc.setFont("helvetica", "normal");
        doc.text(branchAlloc.allocated_schedule, 70, yPos);
        yPos += 15;
        
        // Scope Inclusions (only on first page)
        if (branchIndex === 0) {
          doc.setFont("helvetica", "bold");
          doc.text("Scope Inclusions:", 20, yPos);
          yPos += 7;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          const inclusionLines = doc.splitTextToSize(data.scope_inclusions, 170);
          doc.text(inclusionLines, 20, yPos);
          yPos += inclusionLines.length * 5 + 10;
          
          // Check if we need more space
          if (yPos > 250) {
            yPos = 250; // Limit to avoid overflow
          }
          
          // Scope Exclusions
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text("Scope Exclusions:", 20, yPos);
          yPos += 7;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          const exclusionLines = doc.splitTextToSize(data.scope_exclusions, 170);
          doc.text(exclusionLines, 20, yPos);
          yPos += exclusionLines.length * 5 + 10;
          
          // Check if we need more space for client notes
          if (yPos > 250) {
            doc.addPage();
            yPos = 20;
          }
          
          // Additional Notes from Client (Project Start)
          doc.setFontSize(12);
          doc.setFont("helvetica", "bold");
          doc.text("Additional notes from client (Project start):", 20, yPos);
          yPos += 7;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          
          const clientNotesText = (data.client_notes_project_start && data.client_notes_project_start !== "N/A") 
            ? data.client_notes_project_start 
            : "No additional notes provided";
          const clientNotesLines = doc.splitTextToSize(clientNotesText, 170);
          doc.text(clientNotesLines, 20, yPos);
        }
      });
      
      // Save PDF
      doc.save(`Project_Assign_Form_${data.project_number.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`);
      
      toast.success("PDF downloaded successfully");
      setProjectAssignDialogOpen(false);
    }
  };

  const calculatePlanningStatus = (step) => {
    const stepName = step.step_name;
    
    if (stepName === "Approval" || stepName.startsWith("Re-approval")) {
      if (!step.planned_date && !step.actual_date) return "Yet to start";
      else if (step.planned_date && !step.actual_date) return "Planned";
      else if (step.actual_date) return "Submitted";
    }
    
    if (stepName === "Back from Approval" || stepName.startsWith("Back from Re-approval")) {
      if (!step.received_date) return "Waiting";
      else return "Received";
    }
    
    if (stepName === "Fabrication" || stepName.startsWith("Fabrication ")) {
      if (!step.planned_date && !step.actual_date) return "Yet to start";
      else if (step.planned_date && !step.actual_date) return "Planned";
      else if (step.actual_date) {
        if (step.submission_complete === "Partial") return "Partially submitted";
        else if (step.submission_complete === "Full") return "Submitted";
        return "Planned";
      }
    }
    
    return step.planning_status || "Yet to start";
  };

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-md3-display-small">Reports</h1>
          <p className="text-md3-on-surface-variant mt-1">Generate and view various project reports</p>
        </div>

        {/* Project Reports Section */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">Project Reports</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Project Schedule Tracker Report Card */}
            <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0" onClick={openScheduleReportDialog}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-md3-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Project Schedule Tracker</CardTitle>
                    <CardDescription>Complete submittal schedule report</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-md3-body-small text-md3-on-surface-variant">
                  View or download complete project schedule tracker with all submittals, steps, and statuses.
                </p>
              </CardContent>
            </Card>

            {/* Submittal Planned Report Card */}
            <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0" onClick={openPlannedReportDialog}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-md3-lg">
                    <Calendar className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Submittal Planned</CardTitle>
                    <CardDescription>Planned submittals by date range</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-md3-body-small text-md3-on-surface-variant">
                  View all submittals planned within a specific date range across all projects.
                </p>
              </CardContent>
            </Card>

            {/* Project Assign Form Report Card */}
            <Card className="cursor-pointer hover:shadow-lg transition-shadow bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0" onClick={openProjectAssignDialog}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-md3-lg">
                    <ClipboardList className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">Project Assign Form</CardTitle>
                    <CardDescription>Project assignment details</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-md3-body-small text-md3-on-surface-variant">
                  View or download project assignment form with client, allocation, and scope details.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Report Selection Dialog */}
        <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Project Schedule Tracker Report</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Select a project to generate the schedule tracker report
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="project" className="text-md3-label-large text-gray-700 font-medium">Select Project *</Label>
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger id="project">
                    <SelectValue placeholder="Choose a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.project_number} - {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleViewReport}
                  disabled={!selectedProjectId || loadingReport}
                  className="flex-1 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  View Report
                </Button>
                <Button
                  onClick={handleDownloadReport}
                  disabled={!selectedProjectId || loadingReport}
                  variant="outline"
                  className="flex-1 border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download CSV
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Report Dialog */}
        <Dialog open={viewReportOpen} onOpenChange={setViewReportOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Project Schedule Tracker Report</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {reportData && (
                  <>
                    Project: {reportData.project.project_number} - {reportData.project.name} | 
                    Generated: {format(new Date(reportData.generated_at), 'MM/dd/yyyy hh:mm a')}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            {reportData && (
              <div className="space-y-6 mt-4">
                {reportData.submittals.map((submittal) => (
                  <Card key={submittal.id} className="border-2">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg text-md3-title-medium text-md3-on-surface">{submittal.submittal_name}</CardTitle>
                          <div className="flex items-center gap-2 mt-2">
                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                              submittal.current_status === "App-Progress" ? "bg-blue-100 text-blue-700" :
                              submittal.current_status === "BFA waiting" ? "bg-yellow-100 text-yellow-700" :
                              submittal.current_status === "Fab-Progress" ? "bg-purple-100 text-purple-700" :
                              submittal.current_status === "Completed" ? "bg-green-100 text-green-700" :
                              submittal.current_status === "HOLD due to RFI" ? "bg-orange-100 text-orange-700" :
                              submittal.current_status === "HOLD by Client" ? "bg-red-100 text-red-700" :
                              "bg-md3-surface-container text-md3-on-surface-variant"
                            }`}>
                              {submittal.current_status}
                            </span>
                            {submittal.hold_enabled && (
                              <span className="text-xs text-md3-on-surface-variant">
                                (HOLD: {submittal.hold_reason})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Table className="bg-md3-surface-container rounded-md3-md">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Step</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Planned Date</TableHead>
                            <TableHead>Actual Date</TableHead>
                            <TableHead>Received Date</TableHead>
                            <TableHead>Return Status</TableHead>
                            <TableHead>Submission Complete</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {submittal.steps.map((step, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{step.step_name}</TableCell>
                              <TableCell>
                                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                                  calculatePlanningStatus(step) === "Yet to start" ? "bg-md3-surface-container text-md3-on-surface-variant" :
                                  calculatePlanningStatus(step) === "Planned" ? "bg-blue-100 text-blue-700" :
                                  calculatePlanningStatus(step) === "Waiting" ? "bg-yellow-100 text-yellow-700" :
                                  calculatePlanningStatus(step) === "Received" ? "bg-green-100 text-green-700" :
                                  calculatePlanningStatus(step) === "Partially submitted" ? "bg-orange-100 text-orange-700" :
                                  calculatePlanningStatus(step) === "Submitted" ? "bg-green-100 text-green-700" :
                                  "bg-md3-surface-container text-md3-on-surface-variant"
                                }`}>
                                  {calculatePlanningStatus(step)}
                                </span>
                              </TableCell>
                              <TableCell>{step.planned_date ? format(new Date(step.planned_date), 'MM/dd/yyyy') : '-'}</TableCell>
                              <TableCell>{step.actual_date ? format(new Date(step.actual_date), 'MM/dd/yyyy') : '-'}</TableCell>
                              <TableCell>{step.received_date ? format(new Date(step.received_date), 'MM/dd/yyyy') : '-'}</TableCell>
                              <TableCell>{step.return_status || '-'}</TableCell>
                              <TableCell>{step.submission_complete || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Submittal Planned Report Selection Dialog */}
        <Dialog open={plannedReportDialogOpen} onOpenChange={setPlannedReportDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Submittal Planned Report</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Select a date range to view all planned submittals
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date" className="text-md3-label-large text-gray-700 font-medium">Start Date *</Label>
                  <DatePicker
                    id="start_date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date" className="text-md3-label-large text-gray-700 font-medium">End Date *</Label>
                  <DatePicker
                    id="end_date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleViewPlannedReport}
                  disabled={!startDate || !endDate || loadingReport}
                  className="flex-1 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  View Report
                </Button>
                <Button
                  onClick={handleDownloadPlannedReport}
                  disabled={!startDate || !endDate || loadingReport}
                  variant="outline"
                  className="flex-1 border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download CSV
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Submittal Planned Report Dialog */}
        <Dialog open={viewPlannedReportOpen} onOpenChange={setViewPlannedReportOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Submittal Planned Report</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {plannedReportData && (
                  <>
                    Date Range: {format(new Date(plannedReportData.start_date), 'MM/dd/yyyy')} - {format(new Date(plannedReportData.end_date), 'MM/dd/yyyy')} | 
                    Generated: {format(new Date(plannedReportData.generated_at), 'MM/dd/yyyy hh:mm a')} | 
                    Total Items: {plannedReportData.items.length}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            {plannedReportData && (
              <div className="mt-4">
                {plannedReportData.items.length === 0 ? (
                  <div className="text-center py-8 text-md3-on-surface-variant">
                    No planned submittals found in the selected date range.
                  </div>
                ) : (
                  <Table className="bg-md3-surface-container rounded-md3-md">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Project Number</TableHead>
                        <TableHead>Project Name</TableHead>
                        <TableHead>Submittal Name</TableHead>
                        <TableHead>Step</TableHead>
                        <TableHead>Planned Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {plannedReportData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{item.project_number}</TableCell>
                          <TableCell>{item.project_name}</TableCell>
                          <TableCell>{item.submittal_name}</TableCell>
                          <TableCell>{item.step_name}</TableCell>
                          <TableCell>{format(new Date(item.planned_date), 'MM/dd/yyyy')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Project Assign Form Report Selection Dialog */}
        <Dialog open={projectAssignDialogOpen} onOpenChange={setProjectAssignDialogOpen}>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Project Assign Form Report</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Select a project to generate the assignment form report
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Select Project *</Label>
                <Popover open={projectComboOpen} onOpenChange={setProjectComboOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full w-full justify-between" role="combobox"
                      aria-expanded={projectComboOpen}
                      
                    >
                      {selectedProjectForAssign
                        ? `${projects.find((p) => p.id === selectedProjectForAssign)?.project_number} - ${projects.find((p) => p.id === selectedProjectForAssign)?.name}`
                        : "Search and select project..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0">
                    <Command>
                      <CommandInput placeholder="Search by project number or name..." />
                      <CommandList>
                        <CommandEmpty>No project found.</CommandEmpty>
                        <CommandGroup>
                          {projects.map((project) => (
                            <CommandItem
                              key={project.id}
                              value={`${project.project_number} ${project.name}`}
                              onSelect={() => {
                                setSelectedProjectForAssign(project.id);
                                setProjectComboOpen(false);
                              }}
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${
                                  selectedProjectForAssign === project.id
                                    ? "opacity-100"
                                    : "opacity-0"
                                }`}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{project.project_number}</span>
                                <span className="text-xs text-md3-on-surface-variant">{project.name}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleViewProjectAssign}
                  disabled={!selectedProjectForAssign || loadingReport}
                  className="flex-1 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  View Report
                </Button>
                <Button
                  onClick={handleDownloadProjectAssignPDF}
                  disabled={!selectedProjectForAssign || loadingReport}
                  variant="outline"
                  className="flex-1 border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Project Assign Form Report Dialog */}
        <Dialog open={viewProjectAssignOpen} onOpenChange={setViewProjectAssignOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Project Assign Form Report</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {projectAssignData && (
                  <>
                    Generated: {format(new Date(projectAssignData.generated_at), 'MM/dd/yyyy hh:mm a')}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            {projectAssignData && (
              <div className="space-y-6 mt-4">
                {/* Project Basic Information */}
                <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                  <CardHeader>
                    <CardTitle className="text-md3-title-large text-md3-on-surface ">Project Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-md3-on-surface-variant ">Project Number</Label>
                        <p className="text-md3-title-large mt-1">{projectAssignData.project_number}</p>
                      </div>
                      <div>
                        <Label className="text-md3-on-surface-variant ">Project Name</Label>
                        <p className="text-md3-title-large mt-1">{projectAssignData.project_name}</p>
                      </div>
                    </div>

                    <div>
                      <Label className="text-md3-on-surface-variant ">Client Name</Label>
                      <p className="text-md3-title-large mt-1">{projectAssignData.client_name}</p>
                    </div>

                    <div>
                      <Label className="text-md3-on-surface-variant ">Software</Label>
                      <p className="text-md3-title-large mt-1">{projectAssignData.software}</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Branch Allocations */}
                {projectAssignData.branch_allocations && projectAssignData.branch_allocations.map((branchAlloc, index) => (
                  <Card key={index} className="border-2 border-purple-200">
                    <CardHeader className="bg-purple-50">
                      <CardTitle className="text-md3-title-large text-md3-on-surface ">Branch: {branchAlloc.branch_name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-md3-on-surface-variant ">Allocated Hours</Label>
                          <p className="text-md3-title-large mt-1">{branchAlloc.allocated_hours} hours</p>
                        </div>
                        <div>
                          <Label className="text-md3-on-surface-variant ">Allocated Schedule</Label>
                          <p className="text-md3-title-large mt-1">{branchAlloc.allocated_schedule}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Scope Information */}
                <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                  <CardHeader>
                    <CardTitle className="text-md3-title-large text-md3-on-surface ">Scope Inclusions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-md3-body-small whitespace-pre-wrap">{projectAssignData.scope_inclusions}</p>
                  </CardContent>
                </Card>

                <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                  <CardHeader>
                    <CardTitle className="text-md3-title-large text-md3-on-surface ">Scope Exclusions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-md3-body-small whitespace-pre-wrap">{projectAssignData.scope_exclusions}</p>
                  </CardContent>
                </Card>

                {/* Additional Notes from Client */}
                <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
                  <CardHeader>
                    <CardTitle className="text-md3-title-large text-md3-on-surface ">Additional notes from client (Project start)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {projectAssignData.client_notes_project_start && projectAssignData.client_notes_project_start !== "N/A" ? (
                      <p className="text-md3-body-small whitespace-pre-wrap">{projectAssignData.client_notes_project_start}</p>
                    ) : (
                      <p className="text-md3-body-small text-md3-on-surface-variant italic">No additional notes provided</p>
                    )}
                  </CardContent>
                </Card>

                <div className="flex justify-end pt-4">
                  <Button onClick={() => {
                    setViewProjectAssignOpen(false);
                    handleDownloadProjectAssignPDF();
                  }} className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                    <Download className="mr-2 h-4 w-4" />
                    Download as PDF
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
